/* ==========================================================================
   Rook UI Core — Collapsible Controller
   Web Component: <rk-collapsible-element>
   ========================================================================== */

import { RkComponent } from '@rook/component';

class RkCollapsibleElement extends RkComponent {
    static hydration = 'idle';

    #externalToggleHandler = null;
    #hideTimeout = null;

    setup() {
        this.trigger = this.querySelector('.rk-collapsible__trigger');
        this.content = this.querySelector('.rk-collapsible__content');
        this.isOpen = false;

        if (!this.content) return;

        const defaultOpen = this.hasAttribute('data-open') && this.getAttribute('data-open') !== 'false';
        this.isOpen = defaultOpen;

        this.#syncState(false);

        this.delegate({
            'click .rk-collapsible__trigger': this.onClick,
            'rk:collapsible:open': () => this.open(),
            'rk:collapsible:close': () => this.close(),
            'rk:collapsible:toggle': () => this.toggle(),
        });

        const id = this.getAttribute('id');
        if (id) {
            this.#externalToggleHandler = (event) => this.#handleExternalToggle(event);
            document.addEventListener('click', this.#externalToggleHandler);
        }
    }

    teardown() {
        if (this.#externalToggleHandler) {
            document.removeEventListener('click', this.#externalToggleHandler);
            this.#externalToggleHandler = null;
        }
        if (this.#hideTimeout) {
            clearTimeout(this.#hideTimeout);
            this.#hideTimeout = null;
        }
    }

    open() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.#syncState(true);
    }

    close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.#syncState(true);
    }

    toggle() {
        if (this.isOpen) this.close();
        else this.open();
    }

    onClick(event) {
        event.preventDefault();
        if (this.trigger && this.trigger.disabled) return;
        this.toggle();
    }

    #handleExternalToggle(event) {
        const externalBtn = event.target.closest(`[data-collapsible-toggle="${this.id}"]`);
        if (externalBtn) {
            event.preventDefault();
            this.toggle();
        }
    }

    #syncState(animate = true) {
        if (this.isOpen) {
            this.classList.add('rk-collapsible--open');
            this.content.removeAttribute('hidden');

            requestAnimationFrame(() => {
                this.content.classList.add('rk-collapsible__content--expanded');
            });
        } else {
            this.classList.remove('rk-collapsible--open');
            this.content.classList.remove('rk-collapsible__content--expanded');

            if (!animate) {
                this.content.setAttribute('hidden', 'true');
            } else {
                clearTimeout(this.#hideTimeout);
                this.#hideTimeout = setTimeout(() => {
                    if (!this.isOpen) {
                        this.content.setAttribute('hidden', 'true');
                    }
                }, 300);
            }
        }

        if (this.trigger) {
            this.trigger.setAttribute('aria-expanded', this.isOpen.toString());
        }

        const focusables = this.content.querySelectorAll('a, button, input, select, textarea, [tabindex]');
        focusables.forEach((el) => {
            if (this.isOpen) {
                el.removeAttribute('tabindex');
            } else {
                el.setAttribute('tabindex', '-1');
            }
        });

        this.rkEmit('collapsible:change', { isOpen: this.isOpen });
    }
}

if (!customElements.get('rk-collapsible-element')) {
    customElements.define('rk-collapsible-element', RkCollapsibleElement);
}
